import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Target, CheckCircle, Clock, Gift, MessageCircle, Heart, Eye, Share2, Users } from 'lucide-react';

const ACTION_ICONS = {
  comment: MessageCircle,
  reaction: Heart,
  watch_second: Eye,
  share: Share2,
  follow_creator: Users,
  tip: Gift,
};

function MissionCard({ mission, onClaim }) {
  const progress = mission.progress || 0;
  const target = mission.target || 1;
  const pct = Math.min(100, Math.round((progress / target) * 100));
  const completed = mission.completed;
  const claimed = mission.claimed;
  const Icon = ACTION_ICONS[mission.action] || Target;

  const displayProgress = mission.action === 'watch_second'
    ? `${Math.floor(progress / 60)}m / ${Math.floor(target / 60)}m`
    : `${progress} / ${target}`;

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
        claimed ? 'border-border/20 opacity-50' : completed ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border/40 bg-card/50'
      }`}
      data-testid={`mission-${mission.mission_id}`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
        claimed ? 'bg-muted/20' : completed ? 'bg-emerald-500/20' : 'bg-muted/30'
      }`}>
        {claimed ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <Icon className="w-4 h-4 text-muted-foreground" />}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{mission.title}</p>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 h-1.5 rounded-full bg-muted/30 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${pct}%`, background: completed ? '#10b981' : '#8b5cf6' }}
            />
          </div>
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">{displayProgress}</span>
        </div>
      </div>

      <div className="flex-shrink-0 text-right">
        {claimed ? (
          <span className="text-[10px] text-emerald-500 font-medium">Claimed</span>
        ) : completed ? (
          <Button
            size="sm"
            onClick={() => onClaim(mission.mission_id)}
            className="h-7 px-3 text-[10px] font-bold bg-emerald-600 hover:bg-emerald-500 text-white"
            data-testid={`claim-${mission.mission_id}`}
          >
            +{mission.xp_reward} XP
          </Button>
        ) : (
          <span className="text-[10px] text-muted-foreground font-mono">+{mission.xp_reward} XP</span>
        )}
      </div>
    </div>
  );
}

export function ListenerMissionPanel() {
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const [missions, setMissions] = useState({ daily: [], weekly: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('daily');

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchMissions = async () => {
      try {
        const res = await fetch(`${API}/listener/missions`, { headers: getAuthHeaders() });
        if (res.ok) setMissions(await res.json());
      } catch {} finally { setLoading(false); }
    };
    fetchMissions();
  }, [isAuthenticated, getAuthHeaders]);

  const handleClaim = async (missionId) => {
    try {
      const res = await fetch(`${API}/listener/missions/${missionId}/claim`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`+${data.xp_reward} XP earned!`);
        setMissions(prev => ({
          daily: prev.daily.map(m => m.mission_id === missionId ? { ...m, claimed: true } : m),
          weekly: prev.weekly.map(m => m.mission_id === missionId ? { ...m, claimed: true } : m),
        }));
      }
    } catch {
      toast.error('Failed to claim reward');
    }
  };

  if (!isAuthenticated || loading) return null;

  const currentMissions = tab === 'daily' ? missions.daily : missions.weekly;
  const completedCount = currentMissions.filter(m => m.completed).length;

  return (
    <Card className="border-border/40 bg-card/50 overflow-hidden" data-testid="mission-panel">
      <div className="p-4 border-b border-border/30">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-violet-400" />
            <h3 className="text-sm font-bold text-foreground">Missions</h3>
          </div>
          <span className="text-[10px] text-muted-foreground">{completedCount}/{currentMissions.length} done</span>
        </div>
        <div className="flex gap-1">
          {['daily', 'weekly'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-colors ${
                tab === t ? 'bg-violet-600/20 text-violet-400' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`mission-tab-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="p-3 space-y-2 max-h-80 overflow-y-auto">
        {currentMissions.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No missions available</p>
        ) : (
          currentMissions.map(m => <MissionCard key={m.mission_id} mission={m} onClaim={handleClaim} />)
        )}
      </div>
    </Card>
  );
}
